import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/**
 * Google signup consent audit trail.
 *
 * Email signups persist consent in the auth user's metadata
 * (`accepted_disclaimer` / `age_confirmed`) at signUp time. The Google path has
 * no signUp payload, so we stash the ticked checkboxes locally right before the
 * OAuth redirect and write an auditable row once the session exists.
 */
const PENDING_KEY = "lupuslogix_pending_signup_consent";
const PENDING_TTL_MS = 30 * 60 * 1000;

export const SIGNUP_CONSENT_TYPE = "signup_consent";

type PendingConsent = { disclaimer: boolean; age18: boolean; at: number; method: string };

export const markPendingSignupConsent = (
  disclaimer: boolean,
  age18: boolean,
  method = "google",
) => {
  try {
    const payload: PendingConsent = { disclaimer, age18, at: Date.now(), method };
    localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch {
    /* best effort */
  }
};

export const clearPendingSignupConsent = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
};

const readPendingSignupConsent = (): PendingConsent | null => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingConsent;
    if (!parsed?.at || Date.now() - parsed.at > PENDING_TTL_MS) {
      clearPendingSignupConsent();
      return null;
    }
    if (!parsed.disclaimer || !parsed.age18) {
      clearPendingSignupConsent();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Best-effort, never-throwing: writes the signup consent acknowledgment for the
 * newly authenticated user. Safe to call on every SIGNED_IN — it no-ops unless a
 * fresh pending consent exists, and the (user_id, disclaimer_type) unique
 * constraint makes repeat writes idempotent.
 */
export const persistPendingSignupConsent = async (userId: string) => {
  const pending = readPendingSignupConsent();
  if (!pending) return;
  try {
    const { error } = await supabase
      .from("disclaimer_acknowledgments")
      .upsert(
        { user_id: userId, disclaimer_type: SIGNUP_CONSENT_TYPE },
        { onConflict: "user_id,disclaimer_type", ignoreDuplicates: true },
      );
    if (error) {
      logger.error("Failed to persist signup consent", error);
      return;
    }
    clearPendingSignupConsent();
  } catch (err) {
    logger.error("Failed to persist signup consent", err);
  }
};
